<script lang="ts">
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import ApiService from '@/services/api.service';

export default defineComponent({
  name: 'SlotDetails',
  props: {
    trigger: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      selectedProfiles: {} as Record<string, any>,
      isLoadingLastAppointment: false,
      isSaving: false,
    };
  },
  computed: {
    ...mapGetters(['selectedSlot', 'selectedPatient', 'profiles', 'currentDuration', 'currentTherapies']),
    doctors() {
      if (!this.selectedSlot) return [];
      const allDoctors = this.selectedSlot.doctors || [];
      // Deduplicate by externalId - multiple Doctor records can have same externalId
      // Keep therapyUUID for duration lookup
      const seen = new Set();
      return allDoctors.filter((doctor: any) => {
        if (seen.has(doctor.externalId)) {
          return false;
        }
        seen.add(doctor.externalId);
        return true;
      });
    },
    // Return all profiles for dropdown - not filtered by doctor
    allProfiles() {
      return this.profiles || [];
    },
    officeUuid() {
      // Extract office UUID from location in store if available
      return this.$store.state.scheduler?.currentOfficeUuid;
    },
    canSave() {
      // Can save without profiles selected - profile is optional
      return this.selectedPatient && this.selectedSlot && !this.isSaving;
    },
  },
  mounted() {
    if (this.selectedSlot && this.selectedPatient) {
      this.loadLastAppointment();
    }
  },
  beforeUnmount() {
    // cleanup
  },
  watch: {
    'trigger': function(newVal, oldVal) {
      if (this.selectedSlot && this.selectedPatient) {
        this.loadLastAppointment();
      }
    }
  },
  methods: {
    async loadLastAppointment() {
      if (!this.selectedPatient || !this.selectedSlot) return;

      const officeUuid = this.$store.state.scheduler?.currentOfficeUuid;
      if (!officeUuid) {
        return;
      }

      this.isLoadingLastAppointment = true;
      try {
        // Load recommended profiles for each doctor in this slot
        for (const doctor of this.doctors) {
          try {
            const response = await ApiService.customRequest({
              method: 'get',
              url: `scheduler/patients/${this.selectedPatient.uuid}/recommended-profile/${officeUuid}/${doctor.externalId}`,
            });

            if (response.data?.profile) {
              this.selectedProfiles[doctor.externalId] = response.data.profile;
            }
          } catch (error) {
            // Profile not found for this doctor, skip
          }
        }
      } finally {
        this.isLoadingLastAppointment = false;
      }
    },
    closeDetails() {
      this.$store.commit('clearSelectedSlot');
      this.selectedProfiles = {};
    },
    getDurationForDoctor(doctor: any): number {
      // Get duration from therapies by therapyUUID
      const therapyUUID = doctor.therapyUUID;
      if (therapyUUID && this.currentTherapies && this.currentTherapies[therapyUUID]) {
        // therapies format: [type, duration, doctorIds]
        return this.currentTherapies[therapyUUID][1];
      }
      // Fallback to default
      return this.currentDuration || 30;
    },
    async saveAppointment() {
      if (!this.canSave) return;

      this.isSaving = true;

      try {
        // Build doctors array with external_id, profile_id, duration, and therapy_uuid (office)
        const doctors = this.doctors.map((doctor: any) => {
          const profile = this.selectedProfiles[doctor.externalId];
          return {
            external_id: String(doctor.externalId), // Ensure string
            profile_id: profile?.id || null,
            duration: parseInt(this.getDurationForDoctor(doctor), 10), // Ensure integer
            therapy_uuid: doctor.therapyUUID, // Office UUID for this doctor's therapy
          };
        });

        // Parse scheduled time from slot without timezone
        // Send as plain datetime string so backend can apply appropriate timezone
        const scheduledTime = this.$dayjs(this.selectedSlot.datetime, 'MM/DD/YYYY hh:mm A').format('YYYY-MM-DD HH:mm:ss');

        const payload = {
          patient_uuid: this.selectedPatient.uuid,
          scheduled_time: scheduledTime,
          doctors,
        };

        const response = await ApiService.customRequest({
          method: 'post',
          url: 'scheduler/create-appointment',
          data: payload,
        });

        if (response.data?.success) {
          const appointmentsCount = response.data?.appointments?.length || 0;
          const hasErrors = response.data?.errors?.length > 0;

          // Show success message
          let successMessage = `Appointment${appointmentsCount > 1 ? 's' : ''} created successfully!`;
          if (appointmentsCount > 1) {
            successMessage = `${appointmentsCount} appointments created successfully!`;
          }

          (this as any).$eventBus?.emit('emit-success', {
            state: true,
            text: successMessage
          });

          // If there were partial errors, show warning
          if (hasErrors) {
            setTimeout(() => {
              (this as any).$eventBus?.emit('emit-error', {
                state: true,
                text: `Warning: ${response.data.errors.join('; ')}`
              });
            }, 2000);
          }

          // Close details panel and reset
          this.closeDetails();
          // Reset slots to refresh search
          this.$store.commit('resetSlots');
        } else {
          // Complete failure
          const errorMessages = response.data?.errors?.join('; ') || 'Failed to create appointment';
          throw new Error(errorMessages);
        }
      } catch (error: any) {
        // Show error message
        let errorText = 'Error creating appointment';

        if (error.response?.data?.errors) {
          errorText = error.response.data.errors.join('; ');
        } else if (error.response?.data?.message) {
          errorText = error.response.data.message;
        } else if (error.message) {
          errorText = error.message;
        }

        (this as any).$eventBus?.emit('emit-error', {
          state: true,
          text: errorText
        });
      } finally {
        this.isSaving = false;
      }
    },
  },
});
</script>

<template>
  <div v-if="selectedSlot" class="slotDetailsContainer">
    <div class="detailsHeader">
      <div class="headerLeft">
        <v-btn
          icon
          size="small"
          variant="text"
          @click="closeDetails"
          class="mr-2"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <h2 class="detailsTitle">Appointment Details</h2>
      </div>
      <v-btn
        icon
        size="small"
        variant="text"
        @click="closeDetails"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <div class="detailsContent">
      <div class="infoSection">
        <div class="infoItem">
          <span class="infoLabel">Date & Time:</span>
          <span class="infoValue">{{ selectedSlot?.datetime }}</span>
        </div>

        <div v-if="selectedPatient" class="infoItem">
          <span class="infoLabel">Patient:</span>
          <span class="infoValue">{{ selectedPatient?.name }}</span>
        </div>
      </div>

      <div class="doctorsSection">
        <h3 class="sectionTitle">Doctors & Profiles</h3>

        <div v-if="isLoadingLastAppointment" class="loadingState">
          <v-progress-circular indeterminate size="24" width="2" />
          <span class="ml-2">Loading last appointment...</span>
        </div>

        <div v-for="doctor in doctors" :key="doctor.externalId" class="doctorItem">
          <div class="doctorHeader" :style="{ borderLeftColor: doctor.color }">
            <div class="doctorName">{{ doctor.name }}</div>
          </div>

          <!-- Overlap warning -->
          <div v-if="doctor.isOverlap" class="overlapWarning">
            <v-icon size="small" color="warning" class="mr-1">mdi-alert</v-icon>
            <span>This doctor has another appointment at this time (overlap allowed)</span>
          </div>

          <div class="profileSelector">
            <label class="profileLabel">Appointment Profile (optional):</label>
            <v-select
              v-model="selectedProfiles[doctor.externalId]"
              :items="allProfiles"
              item-title="name"
              item-value="id"
              return-object
              density="compact"
              variant="outlined"
              placeholder="No profile (optional)"
              clearable
              hide-details
              class="profileSelect"
            ></v-select>
          </div>
        </div>
      </div>

      <div class="actionsSection">
        <v-btn
          color="primary"
          size="large"
          variant="flat"
          block
          :disabled="!canSave"
          :loading="isSaving"
          @click="saveAppointment"
        >
          <v-icon class="mr-2">mdi-calendar-check</v-icon>
          Book Appointment
        </v-btn>
      </div>
    </div>
  </div>
</template>

<style scoped>
.slotDetailsContainer {
  background: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.detailsHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
}

.headerLeft {
  display: flex;
  align-items: center;
}

.detailsTitle {
  font-size: 18px;
  font-weight: 700;
  color: #111;
  margin: 0;
}

.detailsContent {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.infoSection {
  margin-bottom: 24px;
}

.infoItem {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}

.infoLabel {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.infoValue {
  font-size: 14px;
  color: #111;
  font-weight: 500;
}

.sectionTitle {
  font-size: 16px;
  font-weight: 700;
  color: #111;
  margin-bottom: 16px;
}

.loadingState {
  display: flex;
  align-items: center;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #666;
}

.doctorsSection {
  margin-bottom: 24px;
}

.doctorItem {
  margin-bottom: 20px;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 8px;
  border-left: 4px solid #ccc;
}

.doctorHeader {
  margin-bottom: 12px;
  padding-left: 4px;
}

.doctorName {
  font-size: 15px;
  font-weight: 600;
  color: #111;
}

.profileSelector {
  margin-top: 12px;
}

.profileLabel {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #555;
  margin-bottom: 8px;
}

.profileSelect {
  background: white;
}

.profileColorDot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
}

.selectedProfileDisplay {
  display: flex;
  align-items: center;
}

.overlapWarning {
  display: flex;
  align-items: center;
  background-color: #fff3e0;
  border: 1px solid #ffb74d;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #e65100;
}

.actionsSection {
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}
</style>





